import React, { useState, useRef, useEffect, useCallback } from "react";
import { Map } from "immutable";
import { useDrag, useDrop } from "react-dnd";
import cx from "classnames";
import { Box, MenuItem, Separator } from "@showitinc/showit-wc";

import BlockUtils from "../../../utils/BlockUtils";
import EventUtils from "../../../utils/EventUtils";
import AppConstants from "../../../constants/AppConstants";
import UIActions from "../../../actions/UIActions";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import LayerPanelElement from "./LayerPanelElement";
import EmptyLayer from "./EmptyLayer";
import { generateDragSpec, generateDropSpec, getTargetArea } from "../../../utils/DnDUtils";
import { BLOCK_STATE_LAYER_ITEM, ELEMENT_LAYER_ITEM } from "../../../constants/DnDConstants";
import { pagePanelDrop } from "../SitePanelDropHandlers";
import ContextMenuWrapper from "../../../app2components/ContextMenus/ContextMenuWrapper";
import uiPrefix from "../../../utils/UiPrefixUtils";
import slugify from "../../../utils/SlugifyUtils";
import openRowContextMenu from "../../../utils/openRowContextMenu";
import { GROUP } from "@/constants/DataTypes";
import LayerPanelGroup from "./LayerPanelGroup";
import { useGroupPanelOpen } from "@/hooks/useGroupPanelOpen";
import { DROP_AREA_TOP } from "@/constants/LayerConstants";
import IDUtils from "@/utils/IDUtils";

import styles from "./LayerPanelBlockState.module.css";

const {
  ITEM_TYPE_ELEMENT,
  ITEM_TYPE_STATE,
  DROP_SHADOW_CENTER,
  DROP_CONTAINER_BACKGROUND,
  DROP_SHADOW_TOP,
  ITEM_TYPE_GROUP,
  DROP_SHADOW_BOTTOM,
} = AppConstants;

const LayerPanelBlockState = ({
  block = Map(),
  id,
  index,
  isBlogState,
  layerType,
  onLayerMove,
  onSelect,
  onVisibleChange,
  selected,
  state,
  displayOnly,
  theme,
  selectedChildren,
  setSelectedChildren,
  selectedCanvasView,
  setSelectedCanvasView,
  nonCanvasViewElementSelected,
}) => {
  const [dropArea, setDropArea] = useState(null);
  const [isExpandedBlogState, setExpandedBlogState] = useState(isBlogState);
  const [isGroupOpen, setIsGroupOpen] = useGroupPanelOpen(id);
  const [isOpened, setIsOpened] = useState(false);
  const selfRef = useRef();
  const name = state.get("name");

  const _deselectChildren = useCallback(() => {
    const updatedSelectedChildren = { ...selectedChildren };

    Object.keys(updatedSelectedChildren).forEach((key) => {
      if (key.includes(id)) {
        updatedSelectedChildren[key] = false;
      }
    });

    setSelectedChildren(updatedSelectedChildren);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) {
      setIsOpened(false);
      _deselectChildren();
    }
  }, [_deselectChildren, id, selected]);

  // Open the first canvas view the first time
  useEffect(() => {
    setIsOpened(true);
  }, []);

  const drag = generateDragSpec({
    type: BLOCK_STATE_LAYER_ITEM,
    item: {
      id,
      index,
      layerType,
    },
    onDrop: ({ data }) => onLayerMove(data),
  });
  const drop = generateDropSpec({
    type: [BLOCK_STATE_LAYER_ITEM, ELEMENT_LAYER_ITEM],
    onHover: (monitor) => setDropArea(getTargetArea(monitor, selfRef.current)),
    onDrop: pagePanelDrop({ dropRef: selfRef.current, id, index, layerType }),
  });

  const [{ dragItemType, isDragging }, dragRef] = useDrag(drag);
  const [{ isOver }, dropRef] = useDrop(drop);

  const _selectElement = (elemId, elemType, e) => {
    const layout = block.getIn(["selected", "layout"]);
    const { multi, range } = EventUtils.selectionType(e);
    isBlogState && !selected && onSelect();

    if (!multi && elemType === GROUP) {
      const groupData = block.getIn(IDUtils.getArray(elemId, 1));
      const groupId = groupData?.get("id");
      groupData && setIsGroupOpen((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    }

    UIActions.selectElement({
      id: elemId,
      type: elemType,
      layout,
      blockId: block.get("id"),
      addTo: multi,
      addRange: range,
      from: "panel",
    });
  };

  const _remove = (e) => {
    e.stopPropagation();
    DocumentStore.dispatch({ type: COMMANDS.REMOVE_BLOCK_STATE, payload: { id } });
  };

  const _rename = (e) => {
    e.stopPropagation();
    UIActions.toggleRenamePopup({
      title: "View",
      itemType: ITEM_TYPE_STATE,
      itemId: id,
      name,
      slug: state.get("slug"),
    });
  };

  const renderers = [];

  if (selected || isExpandedBlogState) {
    const elements = state.get("elements");
    const uiSelected = block.get("selected");

    if (elements?.size > 0) {
      
      elements.forEach((element, idx) => {
        const elemKey = BlockUtils.getElementID(id, idx);
        const elemType = element.get("type");
        const elemId = element.get("id");
        const lastElement = !idx;
 
        const layer = (
          <LayerPanelElement
            key={elemKey}
            elemKey={elemKey}
            id={elemId}
            index={idx}
            layerType={elemType === GROUP ? ITEM_TYPE_GROUP : ITEM_TYPE_ELEMENT}
            isGroupOpen={!!isGroupOpen[elemId]}
            element={element}
            onSelect={(e) => _selectElement(elemKey, elemType, e)}
            onLayerMove={onLayerMove}
            onVisibleChange={onVisibleChange}
            selected={uiSelected}
            stateChild
            theme={theme}
            selectedChildren={selectedChildren}
            setSelectedChildren={setSelectedChildren}
            selectedCanvasView={selectedCanvasView}
            setSelectedCanvasView={setSelectedCanvasView}
            canvasViewId={id}
            isCanvasViewOpened={isOpened}
            setIsCanvasViewOpened={setIsOpened}
            nonCanvasViewElementSelected={nonCanvasViewElementSelected}
            siblingElements={elements}
          />
        );
        if (element.get("type") === GROUP) {
          renderers.unshift(
            <LayerPanelGroup
              groupLayer={layer}
              group={element}
              isGroupOpen={!!isGroupOpen[elemId]}
              key={`${elemId}-wrapper`}
              elemKey={elemKey}
              id={elemId}
              index={idx}
              element={element}
              onSelect={(e) => _selectElement(elemKey, elemType, e)}
              onLayerMove={onLayerMove}
              onVisibleChange={onVisibleChange}
              selected={uiSelected}
              stateChild
              theme={theme}
              selectedChildren={selectedChildren}
              setSelectedChildren={setSelectedChildren}
              selectedCanvasView={selectedCanvasView}
              setSelectedCanvasView={setSelectedCanvasView}
              canvasViewId={id}
              isCanvasViewOpened={isOpened}
              setIsCanvasViewOpened={setIsOpened}
              nonCanvasViewElementSelected={nonCanvasViewElementSelected}
              siblingElements={elements}
              showBottomDropArea={lastElement}
            />
          );
        } else {
          renderers.unshift(layer);
        }
      });
    } else {
      renderers.push(<EmptyLayer key="empty" theme={theme} />);
    }
  }

  const _handleSelect = () => {
    if (selected && !isOpened) {
      setIsOpened(true);
      setSelectedCanvasView(id);
      return null;
    }
    if (!selected) {
      setIsOpened(true);
      setSelectedCanvasView(id);
    } else {
      setIsOpened(false);
      setSelectedCanvasView(null);
    }
    onSelect();
    isBlogState && setExpandedBlogState(!selected);
    return null;
  };

  const _duplicate = (e) => {
    e.stopPropagation();
    DocumentStore.dispatch({ type: COMMANDS.DUPLICATE_BLOCK_STATE, payload: { id } });
  };

  const blockHeadingStyle = {};

  if (isOver && !isDragging) {
    if (dragItemType === ELEMENT_LAYER_ITEM) {
      blockHeadingStyle.backgroundColor = DROP_CONTAINER_BACKGROUND;
      blockHeadingStyle.boxShadow = DROP_SHADOW_BOTTOM;

      if (!isOpened) {
        blockHeadingStyle.boxShadow = DROP_SHADOW_CENTER;
      }
    } else {
      blockHeadingStyle.boxShadow =
        dropArea === DROP_AREA_TOP ? DROP_SHADOW_TOP : DROP_SHADOW_BOTTOM;
    }
  }

  return (
    <div
      data-e2e={uiPrefix(`canvas-views-${slugify(name)}-${index + 1}`)}
      data-testid="layer-block-wrapper"
      className={cx("layer-block-wrapper", "state-group", { selected })}
      ref={(self) => {
        selfRef.current = self;
        dropRef(self);
      }}
      onContextMenu={(e) =>
        openRowContextMenu({
          event: e,
          rowEl: selfRef.current,
          active: selected,
          select: _handleSelect,
        })
      }
    >
      <div
        onClick={_handleSelect}
        ref={dragRef}
        data-testid="layer-block-state"
        className={cx("layer-element", "state-group-header", styles.root, {
          [styles.active]: selected,
        })}
        style={blockHeadingStyle}
      >
        <div className={styles["left-container"]}>
          <div className={styles["canvas-tab-container"]}>
            <div
              className={cx(styles["canvas-tab"], {
                [styles["canvas-tab-selected"]]: selected,
              })}
            >
              <span>{index + 1}</span>
            </div>
          </div>
          <span className={styles.label} onDoubleClick={_rename}>
            {name}
          </span>
        </div>
        <div
          className={cx(styles["menu-container"], {
            [styles["menu-container-active"]]: !isOpened,
          })}

          onClick={(e) => e.stopPropagation()}
        >
          <ContextMenuWrapper
            icon="menuMoreHoriz"
            theme={theme}
            state={theme}
            onClick={(e) => {
              e.stopPropagation();
              if (!selected || !isOpened) _handleSelect();
            }}
            active={selected && isOpened && !displayOnly}
          >
            <MenuItem onClick={_rename}>Rename</MenuItem>
            <MenuItem onClick={_duplicate}>Duplicate</MenuItem>
            <Separator variant="context-menu" />
            <MenuItem onClick={_remove}>
              <Box className={styles.delete}>Delete</Box>
            </MenuItem>
          </ContextMenuWrapper>
        </div>
      </div>
      {!displayOnly ? (
        <div className={selected && isOpened ? styles["renderers-wrapper"] : null}>
          <div style={{ display: isOpened ? "block" : "none" }}>{renderers}</div>
        </div>
      ) : null}
    </div>
  );
};

export default LayerPanelBlockState;
